import { rest } from "msw";
import type { RestHandler, ResponseComposition, RestContext } from "msw";
import config from "../../../config";
import { createUrl } from "../../protect-api";
import {
  apiVersion,
  UserRoles,
  UserStatus,
} from "../../../shared/constants/common.constant";
import { TEAM_ROLE_NAME_BY_ID } from "../team-role.constants";
import type {
  ActiveUser,
  AddTeamMembersPayload,
  DeleteTeamPayload,
  GetTeamMembersResponse,
  Team,
  TeamMember,
  TeamMutationRequest,
  TeamPreferenceResponse,
  TeamsApiResponse,
  UpdatePreferredTeamPayload,
} from "../teams.types";
import { TEAM_STATUS } from "../teams.types";

const HTTP_OK = 200;
const HTTP_ERROR = 404;
const HTTP_CONFLICT = 409;
const HTTP_SERVER_ERROR = 500;
export const MOCK_ASYNC_DELETE_DELAY_MS = 3000;

export const MOCK_TEAM_IDS = {
  untagged: "a1b2c3d4-e5f6-4789-a012-000000000001",
  emea: "a1b2c3d4-e5f6-4789-a012-000000000002",
} as const;

const initialMockTeamsList: Team[] = [
  {
    id: MOCK_TEAM_IDS.untagged,
    name: "Untagged/Default",
    description: "Core Compliance Team Members",
    isDefault: true,
    usersCount: 3,
    createdAt: "2026-06-01T13:50:20.911Z",
    createdById: 0,
    modifiedAt: "2026-06-01T13:50:20.911Z",
    modifiedById: 0,
    status: TEAM_STATUS.Active,
  },
  {
    id: MOCK_TEAM_IDS.emea,
    name: "EMEA Compliance",
    description: "European Compliance Team",
    isDefault: false,
    usersCount: 1,
    createdAt: "2026-06-01T13:50:20.911Z",
    createdById: 0,
    modifiedAt: "2026-06-01T13:50:20.911Z",
    modifiedById: 0,
    status: TEAM_STATUS.Active,
  },
];

const mockTeamsList: Team[] = structuredClone(initialMockTeamsList);

const buildInitialMockTeamMembers = (): Record<string, TeamMember[]> => ({
  [MOCK_TEAM_IDS.untagged]: [
    {
      userId: 89789097,
      firstName: "Alice",
      lastName: "Johnson",
      fullName: "Alice Johnson",
      email: "alice.johnson@company.com",
      roleId: 1,
      roleName: UserRoles.Admin,
    },
    {
      userId: 78909090,
      firstName: "Bob",
      lastName: "Johnson",
      fullName: "Bob Johnson",
      email: "bob.johnson@company.com",
      roleId: 2,
      roleName: UserRoles.Manager,
    },
    {
      userId: 78908790,
      firstName: "Carol",
      lastName: "Johnson",
      fullName: "Carol Johnson",
      email: "carol.johnson@company.com",
      roleId: 3,
      roleName: UserRoles.Analyst,
    },
  ],
  [MOCK_TEAM_IDS.emea]: [
    {
      userId: 12909090,
      firstName: "David",
      lastName: "Johnson",
      fullName: "David Johnson",
      email: "david.johnson@company.com",
      roleId: 4,
      roleName: UserRoles.Viewer,
    },
  ],
});

const mockTeamMembersByTeamId: Record<string, TeamMember[]> =
  buildInitialMockTeamMembers();

export const resetMockTeamsList = (): void => {
  mockTeamsList.splice(
    0,
    mockTeamsList.length,
    ...structuredClone(initialMockTeamsList)
  );
  Object.keys(mockTeamMembersByTeamId).forEach((key) => {
    delete mockTeamMembersByTeamId[key];
  });
  Object.assign(mockTeamMembersByTeamId, buildInitialMockTeamMembers());
  syncAllTeamUsersCounts();
};

const syncTeamUsersCount = (teamId: string): void => {
  const teamIndex = mockTeamsList.findIndex((team) => team.id === teamId);
  if (teamIndex === -1) {
    return;
  }

  mockTeamsList[teamIndex] = {
    ...mockTeamsList[teamIndex],
    usersCount: mockTeamMembersByTeamId[teamId]?.length ?? 0,
  };
};

const syncAllTeamUsersCounts = (): void => {
  mockTeamsList.forEach((team) => {
    syncTeamUsersCount(team.id);
  });
};

const buildTeamsResponse = (): TeamsApiResponse => {
  syncAllTeamUsersCounts();
  return {
    items: [...mockTeamsList],
    count: mockTeamsList.length,
  };
};

const validateTeamPayload = (
  payload: TeamMutationRequest,
  res: ResponseComposition,
  ctx: RestContext
): ReturnType<ResponseComposition> | null => {
  if (!payload.name) {
    return res(
      ctx.status(HTTP_ERROR),
      ctx.json({ message: "Team name is required" })
    );
  }

  if (payload.name === "Duplicate Team") {
    return res(
      ctx.status(HTTP_CONFLICT),
      ctx.json({
        status: HTTP_CONFLICT,
        message: "Team name already exists",
      })
    );
  }

  if (payload.name === "Error Team") {
    return res(
      ctx.status(HTTP_SERVER_ERROR),
      ctx.json({
        status: HTTP_SERVER_ERROR,
        message: "Internal server error",
      })
    );
  }

  return null;
};

const createTeamHandler = (): RestHandler =>
  rest.post(
    createUrl(`${config.KYCP_AUTH_API_URL}/${apiVersion}/teams`),
    async (req, res, ctx) => {
      const payload = await req.json<TeamMutationRequest>();

      const errorResponse = validateTeamPayload(payload, res, ctx);
      if (errorResponse) return errorResponse;

      const newTeam: Team = {
        id: "a1b2c3d4-e5f6-4789-a012-000000000099",
        name: payload.name,
        description: payload.description,
        isDefault: false,
        usersCount: 0,
        createdAt: "2026-06-01T13:50:20.911Z",
        createdById: 0,
        modifiedAt: "2026-06-01T13:50:20.911Z",
        modifiedById: 0,
      };
      mockTeamsList.push(newTeam);

      return res(
        ctx.status(HTTP_OK),
        ctx.json({
          id: newTeam.id,
          name: newTeam.name,
          description: newTeam.description,
        })
      );
    }
  );

export const mockUpdateTeam = (): RestHandler =>
  rest.put(
    createUrl(`${config.KYCP_AUTH_API_URL}/${apiVersion}/teams/:teamId`),
    async (req, res, ctx) => {
      const { teamId } = req.params;
      const teamIdHeader = req.headers.get("X-Team-Id");

      if (!teamIdHeader) {
        return res(
          ctx.status(400),
          ctx.json({ message: "Missing X-Team-Id header" })
        );
      }

      const payload = await req.json<TeamMutationRequest>();

      const errorResponse = validateTeamPayload(payload, res, ctx);
      if (errorResponse) return errorResponse;

      const teamIndex = mockTeamsList.findIndex((t) => t.id === teamId);
      if (teamIndex !== -1) {
        mockTeamsList[teamIndex] = {
          ...mockTeamsList[teamIndex],
          name: payload.name,
          description: payload.description,
        };
      }

      return res(
        ctx.status(HTTP_OK),
        ctx.json({
          id: teamId,
          name: payload.name,
          description: payload.description,
        })
      );
    }
  );

export const mockTeamsData: TeamsApiResponse = {
  items: mockTeamsList,
  count: mockTeamsList.length,
};

export const mockGetTeams = (data?: TeamsApiResponse): RestHandler[] => [
  rest.get(
    createUrl(`${config.KYCP_AUTH_API_URL}/${apiVersion}/teams`),
    (req, res, ctx) => {
      if (data) {
        return res(ctx.status(HTTP_OK), ctx.json(data));
      }

      const page = Number(req.url.searchParams.get("page") ?? 1);
      const limit = Number(req.url.searchParams.get("limit") ?? 10);
      const response = buildTeamsResponse();
      const startIndex = (page - 1) * limit;
      const items = response.items.slice(startIndex, startIndex + limit);

      return res(
        ctx.status(HTTP_OK),
        ctx.json({
          items,
          count: response.count,
        } satisfies TeamsApiResponse)
      );
    }
  ),
  createTeamHandler(),
];

export const mockGetTeamById = (options?: {
  status?: number;
  title?: string;
  type?: string;
}): RestHandler =>
  rest.get(
    createUrl(`${config.KYCP_AUTH_API_URL}/${apiVersion}/teams/:teamId`),
    (req, res, ctx) => {
      const { teamId } = req.params;
      const status = options?.status ?? HTTP_OK;

      if (status === HTTP_ERROR) {
        return res(
          ctx.status(HTTP_ERROR),
          ctx.json({
            title: options?.title ?? "Team Not Found",
            type: "NotFound",
            status: HTTP_ERROR,
          })
        );
      }

      if (status >= 400) {
        return res(
          ctx.status(status),
          ctx.json({ title: options?.title, type: options?.type })
        );
      }

      const team = mockTeamsList.find((item) => item.id === teamId);
      if (!team) {
        return res(
          ctx.status(HTTP_ERROR),
          ctx.json({
            title: "Team Not Found",
            type: "NotFound",
            status: HTTP_ERROR,
          })
        );
      }

      return res(
        ctx.status(HTTP_OK),
        ctx.json({
          id: team.id,
          name: team.name,
          description: team.description,
          isDefault: team.isDefault,
        })
      );
    }
  );
export const mockPutPreferredTeam = (): RestHandler =>
  rest.put(
    createUrl(`${config.KYCP_AUTH_API_URL}/${apiVersion}/preferences/team`),
    async (req, res, ctx) => {
      const payload = await req.json<UpdatePreferredTeamPayload>();

      if (!payload.teamId) {
        return res(
          ctx.status(HTTP_ERROR),
          ctx.json({ message: "Missing team id" })
        );
      }

      const now = "2026-06-01T14:05:43.430Z";

      return res(
        ctx.status(HTTP_OK),
        ctx.json({
          teamId: payload.teamId,
          teamName: "string",
          createdAt: now,
          createdById: 0,
          modifiedAt: now,
          modifiedById: 0,
        })
      );
    }
  );

export const mockPutPreferredTeamError = (): RestHandler =>
  rest.put(
    createUrl(`${config.KYCP_AUTH_API_URL}/${apiVersion}/preferences/team`),
    async (_, res, ctx) => {
      return res(
        ctx.status(HTTP_SERVER_ERROR),
        ctx.json({ message: "Unexpected server error" })
      );
    }
  );

export const mockGetTeamPreference = (
  response: TeamPreferenceResponse
): RestHandler =>
  rest.get(
    createUrl(`${config.KYCP_AUTH_API_URL}/${apiVersion}/preferences/team`),
    async (_, res, ctx) => {
      return res(ctx.status(HTTP_OK), ctx.json(response));
    }
  );

export const mockGetTeamPreferenceError = (): RestHandler =>
  rest.get(
    createUrl(`${config.KYCP_AUTH_API_URL}/${apiVersion}/preferences/team`),
    async (_, res, ctx) => {
      return res(
        ctx.status(HTTP_SERVER_ERROR),
        ctx.json({ message: "Unexpected server error" })
      );
    }
  );

export const mockGetTeamPreferenceNotFound = (): RestHandler =>
  rest.get(
    createUrl(`${config.KYCP_AUTH_API_URL}/${apiVersion}/preferences/team`),
    async (_, res, ctx) => {
      return res(
        ctx.status(HTTP_ERROR),
        ctx.json({ message: "Team not found" })
      );
    }
  );

export const MOCK_ACTIVE_USERS_COUNT = 101;

const NAMED_MOCK_ACTIVE_USERS: ActiveUser[] = [
  {
    userId: 89789097,
    firstName: "Alice",
    lastName: "Johnson",
    fullName: "Alice Johnson",
    email: "alice.johnson@company.com",
    status: UserStatus.Active,
  },
  {
    userId: 78909090,
    firstName: "Bob",
    lastName: "Johnson",
    fullName: "Bob Johnson",
    email: "bob.johnson@company.com",
    status: UserStatus.Active,
  },
  {
    userId: 78908790,
    firstName: "Carol",
    lastName: "Johnson",
    fullName: "Carol Johnson",
    email: "carol.johnson@company.com",
    status: UserStatus.Active,
  },
  {
    userId: 12909090,
    firstName: "David",
    lastName: "Johnson",
    fullName: "David Johnson",
    email: "david.johnson@company.com",
    status: UserStatus.Active,
  },
];

export const MOCK_TEAM_ADD_MEMBERS_TEST_USERS: ActiveUser[] = [
  ...NAMED_MOCK_ACTIVE_USERS,
  {
    userId: 12745090,
    firstName: "User",
    lastName: "3",
    fullName: "User 3",
    email: "user3@123test.com",
    status: UserStatus.Inactive,
  },
];

export const buildMockActiveUsersList = (): ActiveUser[] => {
  const generatedUserCount =
    MOCK_ACTIVE_USERS_COUNT - NAMED_MOCK_ACTIVE_USERS.length;

  return [
    ...NAMED_MOCK_ACTIVE_USERS,
    {
      userId: 12745090,
      firstName: "User",
      lastName: "3",
      fullName: "User 3",
      email: "user3@123test.com",
      status: UserStatus.Inactive,
    },
    ...Array.from({ length: generatedUserCount }, (_, index) => {
      const userNumber = NAMED_MOCK_ACTIVE_USERS.length + index + 1;
      return {
        userId: 10_000_000 + userNumber,
        firstName: "User",
        lastName: `Name ${userNumber}`,
        fullName: `User Name ${userNumber}`,
        email: `user.name.${userNumber}@test.com`,
        status: UserStatus.Active,
      };
    }),
  ];
};

const mockActiveUsersList: ActiveUser[] = buildMockActiveUsersList();

export const mockGetActiveUsers = (
  users: ActiveUser[] = mockActiveUsersList
): RestHandler =>
  rest.get(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/users/active`, true),
    (_req, res, ctx) => {
      const activeUsers = users
        .filter((user) => user.status === UserStatus.Active)
        .map((user) => ({
          userId: user.userId,
          email: user.email,
          firstName: user.firstName,
          surname: user.lastName,
          username: user.fullName,
          isActive: true,
          websiteRole: "",
        }));

      return res(ctx.status(HTTP_OK), ctx.json(activeUsers));
    }
  );

const ROLE_NAME_BY_ID = TEAM_ROLE_NAME_BY_ID;

export const updateMockTeamMemberRoles = (
  updates: Array<{ userId: number; teamId: string; roleId: number }>
): void => {
  updates.forEach(({ userId, teamId, roleId }) => {
    const members = mockTeamMembersByTeamId[teamId];
    if (!members) {
      return;
    }

    const member = members.find((item) => item.userId === userId);
    if (!member) {
      return;
    }

    member.roleId = roleId;
    member.roleName = ROLE_NAME_BY_ID[roleId] ?? UserRoles.Viewer;
  });
};

export const mockAddTeamMembers = (): RestHandler =>
  rest.post(
    createUrl(`${config.KYCP_AUTH_API_URL}/${apiVersion}/teams/:teamId/users`),
    async (req, res, ctx) => {
      const { teamId } = req.params as { teamId: string };
      const teamIdHeader = req.headers.get("X-Team-Id");

      if (!teamIdHeader) {
        return res(
          ctx.status(400),
          ctx.json({ message: "Missing X-Team-Id header" })
        );
      }

      const body = await req.json<AddTeamMembersPayload>();
      const existingMembers = mockTeamMembersByTeamId[teamId] ?? [];
      const newMembers = body
        .filter(
          (item) =>
            !existingMembers.some((member) => member.userId === item.userId)
        )
        .map((item) => {
          const activeUser = mockActiveUsersList.find(
            (user) => user.userId === item.userId
          );
          const roleName = ROLE_NAME_BY_ID[item.roleId] ?? UserRoles.Viewer;
          return {
            userId: item.userId,
            firstName: activeUser?.firstName ?? "User",
            lastName: activeUser?.lastName ?? `${item.userId}`,
            fullName: activeUser?.fullName ?? `User ${item.userId}`,
            email: activeUser?.email ?? `user.${item.userId}@test.com`,
            roleId: item.roleId,
            roleName,
          } satisfies TeamMember;
        });

      mockTeamMembersByTeamId[teamId] = [...existingMembers, ...newMembers];
      syncTeamUsersCount(teamId);

      const successful = body.filter((item) =>
        newMembers.some((member) => member.userId === item.userId)
      );
      const failed = body
        .filter(
          (item) => !successful.some((added) => added.userId === item.userId)
        )
        .map((item) => ({
          failedItem: item,
          error: {
            type: "BadRequest",
            title: "User Already In Team",
            status: 400,
            detail: `User with ID ${item.userId} already exists in team(${teamId}).`,
          },
        }));

      return res(
        ctx.status(HTTP_OK),
        ctx.json({ successful, failed })
      );
    }
  );

export const mockGetTeamMembers = (): RestHandler =>
  rest.get(
    createUrl(`${config.KYCP_AUTH_API_URL}/${apiVersion}/teams/:teamId/users`),
    (req, res, ctx) => {
      const { teamId } = req.params as { teamId: string };
      const pageParam = req.url.searchParams.get("page");
      const limitParam = req.url.searchParams.get("limit");
      const members = mockTeamMembersByTeamId[teamId] ?? [];
      const items =
        pageParam || limitParam
          ? members.slice(
              (Number(pageParam ?? 1) - 1) * Number(limitParam ?? 10),
              (Number(pageParam ?? 1) - 1) * Number(limitParam ?? 10) +
                Number(limitParam ?? 10)
            )
          : members;
      const response: GetTeamMembersResponse = {
        items,
        count: members.length,
      };

      return res(ctx.status(HTTP_OK), ctx.json(response));
    }
  );

export const mockDeleteTeam = (): RestHandler =>
  rest.delete(
    createUrl(`${config.KYCP_AUTH_API_URL}/${apiVersion}/teams/:teamId`),
    async (req, res, ctx) => {
      const { teamId } = req.params;
      const teamIdHeader = req.headers.get("X-Team-Id");

      let actionOnRecords: DeleteTeamPayload["actionOnRecords"] | undefined;
      try {
        const body = await req.json<
          Pick<DeleteTeamPayload, "actionOnRecords">
        >();
        actionOnRecords = body.actionOnRecords;
      } catch {
        actionOnRecords = undefined;
      }

      if (!teamIdHeader) {
        return res(
          ctx.status(400),
          ctx.json({ message: "Missing X-Team-Id header" })
        );
      }

      if (actionOnRecords !== "Delete" && actionOnRecords !== "Move") {
        return res(
          ctx.status(400),
          ctx.json({ message: "Invalid actionOnRecords" })
        );
      }

      const teamIndex = mockTeamsList.findIndex((t) => t.id === teamId);
      if (teamIndex !== -1) {
        if (mockTeamsList[teamIndex].isDefault) {
          return res(
            ctx.status(HTTP_ERROR),
            ctx.json({ message: "Cannot delete default team" })
          );
        }

        const isDataMoved = actionOnRecords === "Move";
        mockTeamsList[teamIndex] = {
          ...mockTeamsList[teamIndex],
          status: isDataMoved ? TEAM_STATUS.MovingData : TEAM_STATUS.Deleting,
        };

        setTimeout(() => {
          const currentIndex = mockTeamsList.findIndex((t) => t.id === teamId);
          if (currentIndex !== -1) {
            mockTeamsList.splice(currentIndex, 1);
          }
        }, MOCK_ASYNC_DELETE_DELAY_MS);
      }

      return res(ctx.status(HTTP_OK));
    }
  );
